import { useState } from 'react';
import { useTranslation } from '@sib/i18n';
import {
  Button,
  cn,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
} from '@sib/ui';
import { X } from '@sib/ui/icons';
import { EditingTag } from '@sib/utils';
import { tagColors } from './TagDialog.utils';

const TAG_NAME_MAX_LENGTH = 26;

export interface TagDialogContentProps {
  tagToEdit: EditingTag | null;
  onSave: (name: string, color: string, uuid?: string) => void;
  isSaving: boolean;
  onRemove?: (uuid: string) => void;
}

export const TagDialogContent = ({
  tagToEdit,
  onSave,
  isSaving,
  onRemove,
}: TagDialogContentProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(tagToEdit?.name ?? '');
  const [color, setColor] = useState(tagToEdit?.color ?? tagColors[0]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave(name.trim(), color, tagToEdit?.uuid);
  };

  const handleRemove = () => {
    if (tagToEdit && onRemove) onRemove(tagToEdit.uuid);
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader className="flex flex-row justify-between">
        <DialogTitle>
          {tagToEdit
            ? t('components.taskForm.fields.tag.editTag')
            : t('components.taskForm.fields.tag.addTag')}
        </DialogTitle>
        <DialogClose>
          <X className="mb-1 size-4 cursor-pointer" />
        </DialogClose>
      </DialogHeader>
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="relative">
            <label className="text-sm font-medium">
              {t('components.taskForm.fields.tag.name')}
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                className="pr-16"
                maxLength={TAG_NAME_MAX_LENGTH}
                autoFocus
              />
            </label>
            <div className="text-muted-foreground absolute top-[70%] right-3 -translate-y-1/2 text-sm">
              {name.length}/{TAG_NAME_MAX_LENGTH}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            {t('components.taskForm.fields.tag.color')}
          </label>
          <div className="grid grid-cols-8 gap-2">
            {tagColors.map(tagColor => (
              <button
                key={`tag-color-${tagColor}`}
                type="button"
                onClick={() => setColor(tagColor)}
                className={cn(
                  'size-8 cursor-pointer rounded-full transition-colors',
                  color === tagColor && 'ring-2 ring-black ring-offset-2'
                )}
                style={{ backgroundColor: tagColor }}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            disabled={!name.trim() || isSaving}
            className="bg-primary w-full"
            onClick={handleSave}
          >
            {t('save')}
          </Button>
          {tagToEdit && onRemove && (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleRemove}
              loading={isSaving}
            >
              {t('components.taskForm.fields.tag.delete')}
            </Button>
          )}
        </div>
      </div>
    </DialogContent>
  );
};
